import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAppData } from '../../contexts/AppDataContext';
import { getDepartments } from '../../utils/departmentUtils';

const DepartmentDistribution = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Map des départements standards pour les libellés
  const departmentLabels = {
    'stock': 'Stock',
    'logistics': 'Logistique',
    'purchasing': 'Achats',
    'sales': 'Ventes',
    'transport': 'Transport',
    'management': 'Direction',
    'it': 'Informatique',
    'accounting': 'Comptabilité',
    'reception': 'Accueil',
    'coaching': 'Coaching',
    'finance': 'Finance',
    'maintenance': 'Maintenance',
    'nettoyage': 'Nettoyage'
  };

  // Charger les employés depuis localStorage
  useEffect(() => {
    const loadEmployees = () => {
      try {
        const storedEmployees = localStorage.getItem('stockpro_employees');
        if (storedEmployees) {
          const parsedEmployees = JSON.parse(storedEmployees);
          setEmployees(parsedEmployees);
          console.log('DepartmentDistribution - Employees loaded:', parsedEmployees.length);
        } else {
          console.log('DepartmentDistribution - No employees in localStorage');
          setEmployees([]);
        }
      } catch (error) {
        console.error('Error loading employees in DepartmentDistribution:', error);
        setEmployees([]);
      }
    };

    // Charger les départements
    const loadDepartments = () => {
      const depts = getDepartments();
      setDepartments(depts);
      console.log('DepartmentDistribution - Departments loaded:', depts);
    };

    loadEmployees();
    loadDepartments();
  }, [refreshTrigger]);

  // Écouter les changements dans localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'stockpro_employees' || e.key === 'stockpro_departments' || !e.key) {
        console.log('DepartmentDistribution detected storage change, refreshing data');
        setRefreshTrigger(prev => prev + 1);
      }
    };
    
    // Écouter les événements de stockage et les événements personnalisés
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleStorageChange);
    };
  }, []);

  // Calculer les statistiques par département
  const departmentStats = useMemo(() => {
    // Si pas d'employés, retourner un tableau vide
    if (!employees.length) return [];
    
    // Compter les employés par département
    const deptCounts = {};
    employees.forEach(employee => {
      if (employee.department) {
        deptCounts[employee.department] = (deptCounts[employee.department] || 0) + 1;
      }
    });
    
    // Créer des statistiques pour tous les départements connus
    const allDepartments = [...new Set([
      ...departments,
      ...Object.keys(deptCounts)
    ])];
    
    // Calculer le pourcentage pour chaque département
    const totalEmployees = employees.length;
    
    return allDepartments.map(dept => {
      const count = deptCounts[dept] || 0;
      const percentage = totalEmployees > 0 ? (count / totalEmployees) * 100 : 0;
      
      return {
        id: dept,
        name: departmentLabels[dept] || dept,
        count,
        percentage: percentage.toFixed(0)
      };
    }).sort((a, b) => b.count - a.count); // Trier par nombre d'employés décroissant
  }, [employees, departments, departmentLabels]);

  // Organiser les départements en rangées de 3
  const departmentRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < departmentStats.length; i += 3) {
      rows.push(departmentStats.slice(i, i + 3));
    }
    return rows;
  }, [departmentStats]);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Répartition par département</h2>
      
      {departmentRows.map((row, rowIndex) => (
        <div key={`row-${rowIndex}`} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {row.map((dept, index) => (
            <motion.div
              key={dept.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20"
            >
              <h3 className="text-lg font-medium text-white">{dept.name}</h3>
              <div className="mt-2">
                <div className="w-full bg-white/10 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full" 
                    style={{ width: `${dept.percentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-sm text-white/70">{dept.count} employé{dept.count !== 1 ? 's' : ''}</span>
                  <span className="text-sm text-white/70">{dept.percentage}%</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ))}
      
      {departmentStats.length === 0 && (
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-center border border-white/20">
          <p className="text-white/60">Aucun département ou employé disponible</p>
        </div>
      )}
    </div>
  );
};

export default DepartmentDistribution;